import { useCallback, useMemo } from "react";

import Button from "~/components/buttons/Button";
import Headline from "~/components/Headline/Headline.tsx";
import StatusIndicatorDot from "~/components/statusDisplay/StatusIndicatorDot";
import TabBar from "~/components/TabBar";
import { ORDER_PARAM_NAME, ORDERSECTION_PARAM_NAME, useSearchParamTab } from "~/hooks/useSearchParamTab.ts";
import type { Quota } from "~/modules/client/api/quota/quotaTypes.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectRole } from "~/modules/project/api/projectRole/projectRoleTypes.ts";
import type { Staffing } from "~/modules/project/api/staffing/staffingTypes.ts";
import { WorkingStatusId } from "~/modules/project/api/workingStatus/workingStatusTypes.ts";
import type {
	OrderDetailsTab,
} from "~/modules/project/components/ProjectDetailsView/components/Orders/ordersViewTypes.ts";
import { ORDER_DETAILS_TABS } from "~/modules/project/components/ProjectDetailsView/components/Orders/ordersViewTypes.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

import OrderDetails from "../OrderDetails/OrderDetails.tsx";

interface Props {
	allUsers: User[];
	clientId: string;
	onNewOrderClick?: () => void;
	phasesOrders: OrderWithBillingSettings[];
	projectPhaseId: string;
	projectRoles: ProjectRole[];
	staffings: Staffing[];
	showBudget: boolean;
	quota?: Quota;
}

const Orders = ({
		allUsers,
		clientId,
		onNewOrderClick,
		phasesOrders,
		projectRoles,
		staffings,
		showBudget,
		quota,
	}: Props) => {
		const [visibleSection, setVisibleSection] = useSearchParamTab<OrderDetailsTab>(
			ORDERSECTION_PARAM_NAME,
			ORDER_DETAILS_TABS,
			"staffings",
		);
		const sortedOrders = useMemo(() => [...phasesOrders].sort(byObjectProperty("id")), [phasesOrders]);
		const sortedOrderIds = useMemo(() => sortedOrders.map((order) => order.id), [sortedOrders]);

		const [selectedOrderId, setSelectedOrderId] = useSearchParamTab<string>(
			ORDER_PARAM_NAME,
			sortedOrderIds,
			sortedOrderIds[0] ?? "",
		);

		const tabs = useMemo(() => {
			return sortedOrders.map((order) => {
				return {
					label: order.title,
					id: order.id,
					icon:
						<StatusIndicatorDot statusId={order.confirmedAt ? WorkingStatusId.Active : WorkingStatusId.Closed} />,
				};
			});
		}, [sortedOrders]);

		const selectedOrderData = phasesOrders.find((order) => order.id === selectedOrderId);
		const selectedOrdersStaffings = staffings.filter((s) => selectedOrderId === s.orderId);
		const hasMultipleOrders = phasesOrders.length > 1;

		const handleOrderDeleteSuccess = useCallback((deletedOrderId: string) => {
			if (phasesOrders.length > 0) {
				const ordersSorted = phasesOrders.filter(order => order.id !== deletedOrderId).sort(byObjectProperty("createdAt", "desc"));
				if (ordersSorted[0]) {
					setSelectedOrderId(ordersSorted[0].id);
				}
			}
		}, [phasesOrders, setSelectedOrderId]);

		return (
			<div className="flex flex-col">
				<div className="flex items-center justify-between">
					<Headline type="h3">{hasMultipleOrders ? "Bestellungen" : "Bestellung"} & Staffing</Headline>
					{onNewOrderClick && <Button size="sm"
												theme="secondary"
												onClick={onNewOrderClick}>Neue Bestellung</Button>}
				</div>
				{phasesOrders.length > 1 ? <div className="my-4">
						<TabBar tabs={tabs}
								activeTabId={selectedOrderId || ""}
								onTabClick={setSelectedOrderId} /></div> :
					<div className="h-4" />
				}
				{selectedOrderData && selectedOrdersStaffings !== undefined && (
					<OrderDetails
						allUsers={allUsers}
						clientId={clientId}
						key={selectedOrderData.id}
						orderData={selectedOrderData}
						onOrderDeleteSuccess={handleOrderDeleteSuccess}
						projectRoles={projectRoles}
						quota={quota}
						setVisibleSection={setVisibleSection}
						showBudget={showBudget}
						staffings={selectedOrdersStaffings}
						totalOrders={phasesOrders.length}
						visibleSection={visibleSection}
					/>
				)}
			</div>
		);
	}
;

export default Orders;
